// ManiDine Restaurant App / src/App.jsx / Updated: 2026-04-19 15:48 MDT
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";

import Login from "./Login.jsx";
import RestaurantDashboard from "./RestaurantDashboard.jsx";
import Settings from "./Settings.jsx";
import RestaurantSignup from "./RestaurantSignup.jsx";
import OrdersPage from "./pages/OrdersPage.jsx";
import MenuManager from "./pages/MenuManager.jsx";
import ReportsRoutes from "./routes/ReportsRoutes.jsx";
import PaymentsPage from "./pages/PaymentsPage.jsx";
import ContactSupportPage from "./pages/ContactSupportPage.jsx";

import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

const NotFoundPage = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#f8fafc",
      color: "#0f172a",
      gap: "0.75rem",
    }}
  >
    <h1 style={{ fontSize: "1.5rem", fontWeight: 600 }}>Page not found</h1>
    <p style={{ color: "#475569" }}>
      The page you’re looking for doesn’t exist. Double-check the URL or pick a link from the
      sidebar.
    </p>
    <a href="/dashboard" style={{ color: "#2563eb", fontWeight: 500 }}>
      Go back to dashboard
    </a>
  </div>
);

const FullPageMessage = ({ title, subtitle }) => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      padding: "24px",
      fontFamily:
        "system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: "560px",
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "16px",
        padding: "28px 24px",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
      }}
    >
      <h1
        style={{
          margin: 0,
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "#0f172a",
        }}
      >
        {title}
      </h1>
      <p
        style={{
          margin: "10px 0 0 0",
          color: "#475569",
          lineHeight: 1.6,
        }}
      >
        {subtitle}
      </p>
    </div>
  </div>
);

const LegalPage = ({ title, children }) => (
  <div
    style={{
      minHeight: "100vh",
      backgroundColor: "#f8fafc",
      padding: "48px 20px",
      fontFamily:
        "system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      color: "#0f172a",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: "860px",
        margin: "0 auto",
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "20px",
        padding: "32px 28px",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
      }}
    >
      <div style={{ marginBottom: "18px" }}>
        <Link
          to="/login"
          style={{
            color: "#2563eb",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          ← Back to login
        </Link>
      </div>

      <h1
        style={{
          margin: "0 0 18px 0",
          fontSize: "2rem",
          fontWeight: 700,
          color: "#0f172a",
        }}
      >
        {title}
      </h1>

      <div style={{ color: "#475569", lineHeight: 1.7, fontSize: "1rem" }}>
        {children}
      </div>
    </div>
  </div>
);

const TermsPage = () => (
  <LegalPage title="Terms of Use">
    <p>
      ManiDine Restaurant Partner Portal is provided for restaurants to manage
      onboarding, orders, menus, payments, reports, and account settings.
    </p>
    <p>
      By using this portal, you agree to provide accurate business information,
      keep your account credentials secure, and use the service only for lawful
      restaurant operations on ManiDine.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      1. International Data Processing
    </h3>
    <p>
      Restaurant agrees that ManiDine may use service providers outside of
      Canada for administrative, verification, and support purposes. By using
      the portal, you consent to the transfer and processing of business
      information in these overseas jurisdictions.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      2. Food Safety &amp; Liability
    </h3>
    <p>
      Restaurant warrants that it holds all necessary Alberta Health Services
      (AHS) permits and follows the Alberta Food Regulation.
    </p>
    <p>
      Restaurant is solely responsible for food safety, packaging, and quality
      until the point of hand-off to the ManiDine courier. ManiDine is a
      logistics platform and is not liable for any health-related claims
      arising from the restaurant&apos;s food.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      3. Stripe &amp; Payment Fees
    </h3>
    <p>
      All payments are processed via Stripe. In addition to ManiDine service
      fees, the Restaurant is responsible for all third-party payment
      processing fees, including standard Stripe transaction fees, which will
      be deducted automatically from each transaction.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      4. Menu Parity
    </h3>
    <p>
      To ensure a positive customer experience, Restaurant agrees to maintain
      price parity between its ManiDine menu and its in-house menu to the best
      of its ability.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      5. General Platform Responsibilities
    </h3>
    <p>
      Restaurants are responsible for the accuracy of menu items, pricing,
      availability, tax settings, fulfillment readiness, and customer-facing
      business information shown through ManiDine.
    </p>
    <p>
      ManiDine may suspend or restrict access where required for fraud
      prevention, policy enforcement, safety, payment compliance, onboarding
      review, or platform integrity.
    </p>
  </LegalPage>
);

const PrivacyPage = () => (
  <LegalPage title="Privacy Policy">
    <p>
      <strong>ManiDine Restaurant Privacy Policy</strong>
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      1. Collection and Use
    </h3>
    <p>
      ManiDine collects business and account information to operate the partner
      portal, facilitate onboarding, process orders, manage payouts via
      Stripe, and provide support. This includes account credentials, menu
      data, and operational records.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      2. Disclosure &amp; International Processing
    </h3>
    <p>
      ManiDine limits access to information to authorized personnel. ManiDine
      may use service providers located overseas for administrative support,
      document verification, and platform maintenance.
    </p>
    <p>
      By using the portal, you acknowledge that your business and account
      information may be processed in overseas jurisdictions for onboarding,
      account management, support, and related operational purposes.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      3. Protection of Information
    </h3>
    <p>
      We employ administrative and technological safeguards, including
      encrypted data transmission and restricted access protocols, to protect
      your information from unauthorized access, loss, or modification.
    </p>
    <p>
      We retain information only as long as necessary for business or legal
      purposes.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      4. Your Rights
    </h3>
    <p>
      Under Alberta&apos;s Personal Information Protection Act (PIPA), you have
      the right to request access to the personal information we hold about
      your business account and to request corrections where appropriate.
    </p>

    <h3 style={{ margin: "1.5rem 0 0.5rem 0", color: "#0f172a" }}>
      5. Contact
    </h3>
    <p>
      Questions regarding our privacy practices or our use of overseas service
      providers should be directed to our Privacy Officer at{" "}
      <a href="mailto:mani@manidine.ca">mani@manidine.ca</a>.
    </p>
  </LegalPage>
);

const normalize = (value) => String(value || "").trim().toLowerCase();

const hasOperationalAccess = (restaurantData) => {
  const onboardingStatus = normalize(restaurantData?.onboardingStatus);
  return onboardingStatus === "approved";
};

const useRestaurantRecord = (sessionUser) => {
  const [restaurantLoading, setRestaurantLoading] = useState(Boolean(sessionUser));
  const [restaurantData, setRestaurantData] = useState(null);

  useEffect(() => {
    if (!sessionUser?.uid) {
      setRestaurantData(null);
      setRestaurantLoading(false);
      return undefined;
    }

    setRestaurantLoading(true);

    const restaurantRef = doc(db, "restaurants", sessionUser.uid);
    const unsubscribe = onSnapshot(
      restaurantRef,
      (snapshot) => {
        setRestaurantData(snapshot.exists() ? snapshot.data() : null);
        setRestaurantLoading(false);
      },
      () => {
        setRestaurantData(null);
        setRestaurantLoading(false);
      }
    );

    return unsubscribe;
  }, [sessionUser?.uid]);

  return { restaurantLoading, restaurantData };
};

const RequireRestaurantProfile = ({ user, authLoading, children }) => {
  const sessionUser = user || auth.currentUser;
  const { restaurantLoading, restaurantData } = useRestaurantRecord(sessionUser);

  if (authLoading && !sessionUser) {
    return (
      <FullPageMessage
        title="Checking your session…"
        subtitle="Please wait while we confirm your restaurant account."
      />
    );
  }

  if (!sessionUser) {
    return <Navigate to="/login" replace />;
  }

  if (!sessionUser.emailVerified) {
    return <Navigate to="/login" replace />;
  }

  if (restaurantLoading) {
    return (
      <FullPageMessage
        title="Loading restaurant profile…"
        subtitle="Please wait while we check whether your restaurant onboarding is complete."
      />
    );
  }

  if (!restaurantData) {
    return <Navigate to="/complete-signup" replace />;
  }

  return children;
};

const RequireSignupCompletion = ({ user, authLoading, children }) => {
  const sessionUser = user || auth.currentUser;
  const { restaurantLoading, restaurantData } = useRestaurantRecord(sessionUser);

  if (authLoading && !sessionUser) {
    return (
      <FullPageMessage
        title="Checking your session…"
        subtitle="Please wait while we confirm your restaurant account."
      />
    );
  }

  if (!sessionUser) {
    return <Navigate to="/login" replace />;
  }

  if (!sessionUser.emailVerified) {
    return <Navigate to="/login" replace />;
  }

  if (restaurantLoading) {
    return (
      <FullPageMessage
        title="Loading onboarding…"
        subtitle="Please wait while we check your restaurant profile."
      />
    );
  }

  if (restaurantData) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const RequireOperationalAccess = ({ user, authLoading, children }) => {
  const sessionUser = user || auth.currentUser;
  const { restaurantLoading, restaurantData } = useRestaurantRecord(sessionUser);

  const canOperate = hasOperationalAccess(restaurantData);

  if (authLoading && !sessionUser) {
    return (
      <FullPageMessage
        title="Checking your session…"
        subtitle="Please wait while we confirm your restaurant account."
      />
    );
  }

  if (!sessionUser) {
    return <Navigate to="/login" replace />;
  }

  if (!sessionUser.emailVerified) {
    return <Navigate to="/login" replace />;
  }

  if (restaurantLoading) {
    return (
      <FullPageMessage
        title="Loading restaurant access…"
        subtitle="Please wait while we verify whether this restaurant account can access operational pages."
      />
    );
  }

  if (!restaurantData) {
    return <Navigate to="/complete-signup" replace />;
  }

  if (!canOperate) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  const showPublicRoute = !user || !user.emailVerified;

  return (
    <Routes>
      <Route
        path="/"
        element={
          showPublicRoute ? <RestaurantSignup /> : <Navigate to="/complete-signup" replace />
        }
      />
      <Route
        path="/login"
        element={showPublicRoute ? <Login /> : <Navigate to="/complete-signup" replace />}
      />
      <Route
        path="/signup"
        element={
          showPublicRoute ? <RestaurantSignup /> : <Navigate to="/complete-signup" replace />
        }
      />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route
        path="/complete-signup"
        element={
          <RequireSignupCompletion user={user} authLoading={authLoading}>
            <RestaurantSignup detailsOnly />
          </RequireSignupCompletion>
        }
      />

      <Route
        path="/dashboard"
        element={
          <RequireRestaurantProfile user={user} authLoading={authLoading}>
            <RestaurantDashboard />
          </RequireRestaurantProfile>
        }
      />
      <Route
        path="/orders"
        element={
          <RequireOperationalAccess user={user} authLoading={authLoading}>
            <OrdersPage />
          </RequireOperationalAccess>
        }
      />
      <Route
        path="/menu/*"
        element={
          <RequireOperationalAccess user={user} authLoading={authLoading}>
            <MenuManager />
          </RequireOperationalAccess>
        }
      />
      <Route
        path="/settings/*"
        element={
          <RequireOperationalAccess user={user} authLoading={authLoading}>
            <Settings />
          </RequireOperationalAccess>
        }
      />
      <Route
        path="/reports/*"
        element={
          <RequireOperationalAccess user={user} authLoading={authLoading}>
            <ReportsRoutes />
          </RequireOperationalAccess>
        }
      />
      <Route
        path="/payments"
        element={
          <RequireOperationalAccess user={user} authLoading={authLoading}>
            <PaymentsPage />
          </RequireOperationalAccess>
        }
      />
      <Route
        path="/support"
        element={
          <RequireOperationalAccess user={user} authLoading={authLoading}>
            <ContactSupportPage />
          </RequireOperationalAccess>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
